import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useLocation, useSearchParams } from "react-router-dom";
import {
  editArticleGroup,
  getArticleGroupById,
  getArticleGroupCountById,
  getArticlesFiltered,
  getBranchOfficesFiltered,
  getBrandsFiltered,
  getCategoriesFiltered,
} from "../../../api/Services";
import {
  hasPermission,
  privilegesEnum,
} from "../../../helpers/PermissionHelper";
import {
  Form,
  Input,
  message,
  Select,
  Switch,
  InputNumber,
  Skeleton,
} from "antd";
import { RxLayers } from "react-icons/rx";
import CustomBreadcrumb from "../../../components/breadcrumb/Breadcrumb";
import ActionButtons from "../../../components/actionbuttons/ActionButtons";

import styles from "./ViewArticleGroup.module.scss";

const ViewArticleGroup = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const getBasePath = () => {
    if (location.pathname.startsWith('/commercial-intelligence')) {
      return '/commercial-intelligence/configuration/article-groups';
    }
    if (location.pathname.startsWith('/promotions')) {
      return '/promotions/articlegroups';
    }
    return '/pricings/articlegroups';
  };

  const { id } = useParams();

  const [articleGroup, setArticleGroup] = useState<any>();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadingArticles, setLoadingArticles] = useState(false);
  const [articles, setArticles] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [brands, setBrands] = useState<any[]>([]);
  const [branchOffices, setBranchOffices] = useState<any[]>([]);

  const handleSearchCategories = async (value: string) => {
    const response = await getCategoriesFiltered(value);
    setCategories(response);
  };

  const getOptionsCategories = (items: any) => {
    const options: any[] = [];

    items?.forEach(function (item: any) {
      const parentsString = item?.parents
        ? item.parents.filter(Boolean).join(" - ")
        : "";
      const categoryName = item?.name || "";

      const categoryTree = parentsString
        ? `${parentsString} - ${categoryName}`
        : categoryName;

      options.push(
        <Select.Option value={item.id} key={item.id}>
          {categoryTree}
        </Select.Option>
      );
    });

    return options;
  };

  const handleSearchBrands = async (value: string) => {
    const response = await getBrandsFiltered(value);
    setBrands(response);
  };

  const handleSearchBranchOffices = async (value: string) => {
    const response = await getBranchOfficesFiltered(0, {
      name: value,
    });
    setBranchOffices(response.branchOffices);
  };
  const getOptions = (items: any) => {
    const options: any[] = [];

    items?.forEach(function (item: any) {
      options.push(
        <Select.Option value={item.id} key={item.id}>
          {item.name}
        </Select.Option>
      );
    });

    return options;
  };

  const getOptionsArticles = (items: any) => {
    const options: any[] = [];

    items?.forEach(function (item: any) {
      options.push(
        <Select.Option value={item.id} key={item.id}>
          {item.name}
        </Select.Option>
      );
    });

    return options;
  };

  const [editForm] = Form.useForm();

  const canEdit = hasPermission(privilegesEnum.WRITE_ARTICLE_GROUP);

  useEffect(() => {
    if (hasPermission(privilegesEnum.READ_ARTICLE_GROUP)) {
      handleFetchArticleGroup();
      handleFetchCount();
    }
  }, []);

  const handleFetchCount = async () => {
    const response = await getArticleGroupCountById(id!);
    editForm.setFieldsValue({ quantity: response });
  };

  const handleFetchArticleGroup = async () => {
    const response = await getArticleGroupById(id!);

    if (response) {
      setArticleGroup(response);
      handleSearchBrands(response.brand ? response.brand.name : "");
      handleSearchCategories(response.category ? response.category.name : "");
      if (response.articles && response.articles.length > 0) {
        setArticles(response.articles);
      }
      handleSearchBranchOffices(
        response.branchOffice ? response.branchOffice.name : ""
      );
      setLoading(false);
    }
  };

  const handleSearchArticles = async (value: string) => {
    setLoadingArticles(true);
    const response = await getArticlesFiltered(0, { articleName: value });
    setArticles(response.articles);
    setLoadingArticles(false);
  };

  const handleEdit = async () => {
    await editForm.validateFields().then(async (values) => {
      setSaving(true);
      const {
        name,
        description,
        state,
        categoryId,
        brandId,
        branchOfficeId,
        articleIds,
      } = values;
      let response = await editArticleGroup(parseInt(id!), {
        name,
        description,
        state,
        categoryId,
        brandId,
        branchOfficeId,
        articleIds,
      });

      if (response) {
        message.success(t("Common-EditSuccessMessage"));
        await handleFetchArticleGroup();
        await handleFetchCount();
      } else message.error(t("Common-EditErrorMessage"));

      setSaving(false);
    });
  };
  const handleCancelButton = () => {
    editForm.resetFields();
  };

  return loading ? (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div>
          <Skeleton.Input style={{ width: 180, height: 20 }} active />
        </div>
        <div className={styles.actionButtonsWrapper}>
          <Skeleton.Button style={{ width: 80, height: 32 }} active />
          <Skeleton.Button style={{ width: 80, height: 32 }} active />
        </div>
      </div>

      <div className={styles.articleGroupInfoWrapper}>
        <Skeleton active paragraph={{ rows: 8 }} />
      </div>
    </div>
  ) : !articleGroup ? (
    <div>Not Found</div>
  ) : (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div>
          <CustomBreadcrumb
            items={[
              {
                icon: <RxLayers />,
                label: t("Common-ArticleGroups"),
                path: `${getBasePath()}?${searchParams.toString()}`
              },
              {
                label: articleGroup?.name || t("Common-ArticleGroup"),
                isCurrentPage: true
              }
            ]}
          />
        </div>
        <ActionButtons
          onSave={handleEdit}
          onCancel={handleCancelButton}
          loading={saving}
          disabled={saving}
          show={true}
          permission={privilegesEnum.WRITE_ARTICLE_GROUP}
        />
      </div>

      <div className={styles.articleGroupInfoWrapper}>
        <Form
          name="articleGroup"
          layout="vertical"
          form={editForm}
          autoComplete="off"
          className={styles.newForm}
          preserve={false}
        >
          <div className={styles.itemWrapper}>
            <Form.Item
              label={t("Common-QuantityArticles")}
              name="quantity"
              className={styles.formItem}
            >
              <InputNumber disabled className={styles.inputNumber} />
            </Form.Item>
            <Form.Item
              initialValue={articleGroup?.name}
              label={t("Common-Name")}
              name="name"
              className={styles.formItem}
              rules={[
                {
                  required: true,
                  message: t("Common-Name"),
                },
              ]}
            >
              <Input placeholder={t("Common-Name")} disabled={!canEdit} />
            </Form.Item>
          </div>
          <div className={styles.itemWrapper}>
            <Form.Item
              initialValue={articleGroup?.description}
              label={t("Common-Description")}
              name="description"
              className={styles.formItem}
              rules={[
                {
                  required: true,
                  message: t("Common-Description"),
                },
              ]}
            >
              <Input placeholder={t("Common-Description")} disabled={!canEdit} />
            </Form.Item>
            <Form.Item
              initialValue={articleGroup?.state}
              label={t("Common-Active")}
              name="state"
              className={styles.formItem}
              rules={[
                {
                  required: true,
                  message: t("Common-Active"),
                },
              ]}
            >
              <Switch defaultChecked={articleGroup?.state} disabled={!canEdit} />
            </Form.Item>
          </div>
          <div className={styles.itemWrapper}>
            <Form.Item
              label={t("Common-Category")}
              name="categoryId"
              className={styles.formItem}
              initialValue={articleGroup.category?.id}
            >
              <Select
                showSearch
                allowClear
                style={{ width: "100%" }}
                onSearch={handleSearchCategories}
                placeholder={t("Common-SearchByName")}
                disabled={!canEdit}
                filterOption={(input: string, option: any) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >=
                  0
                }
              >
                {getOptionsCategories(categories)}
              </Select>
            </Form.Item>
            <Form.Item
              label={t("Common-Brand")}
              initialValue={articleGroup.brand?.id}
              name="brandId"
              className={styles.formItem}
            >
              <Select
                allowClear
                showSearch
                filterOption={(input: string, option: any) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >=
                  0
                }
                style={{ width: "100%" }}
                placeholder={t("Common-SearchByName")}
                onSearch={handleSearchBrands}
                disabled={!canEdit}
              >
                {getOptions(brands)}
              </Select>
            </Form.Item>
          </div>
          <div className={styles.itemWrapper}>
            <Form.Item
              label={t("Common-BranchOffice")}
              name="branchOfficeId"
              initialValue={articleGroup.branchOffice?.id}
              className={styles.formItem}
            >
              <Select
                allowClear
                showSearch
                filterOption={(input: string, option: any) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >=
                  0
                }
                style={{ width: "100%" }}
                placeholder={t("Common-SearchByName")}
                onSearch={handleSearchBranchOffices}
                disabled={!canEdit}
              >
                {getOptions(branchOffices)}
              </Select>
            </Form.Item>
            <div className={styles.formItem}></div>
          </div>
          <div className={styles.itemWrapper}>
            <Form.Item
              label={t("Common-Articles")}
              name="articleIds"
              initialValue={articleGroup.articles?.map((article: any) => article.id)}
              className={styles.formItem}
            >
              <Select
                mode="multiple"
                showSearch
                allowClear
                placeholder={t("Common-SearchArticleTextPlaceholder")}
                loading={loadingArticles}
                filterOption={(input: string, option: any) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >=
                  0
                }
                style={{ width: "100%" }}
                onSearch={handleSearchArticles}
                disabled={!canEdit}
                className={styles.articlesSelect}
                maxTagCount="responsive"
              >
                {getOptionsArticles(articles)}
              </Select>
            </Form.Item>
            <div className={styles.formItem}></div>
          </div>
        </Form>
      </div>
    </div>
  );
};
export default ViewArticleGroup;
